const privacyPolicyContent = {
  title: 'Auction.ai Chrome Extension Privacy Policy',
  lastUpdated: 'June 8, 2026',
  intro: [
    'This Privacy Policy explains how the Auction.ai Price Estimator Chrome extension, referred to as the "Extension," collects, uses, stores, and shares information.',
    'The Extension is provided by American Liquidations LLC, doing business as Auction.ai, referred to in this policy as "Auction.ai," "we," "us," or "our."',
    'This policy applies specifically to the Extension. Other Auction.ai websites, applications, or services may be governed by separate privacy policies.',
  ],
  sections: [
    {
      title: '1. Information the Extension Processes',
      paragraphs: [
        'The information processed by the Extension depends on the features you choose to use.',
      ],
      subsections: [
        {
          title: 'A. Account and Authentication Information',
          paragraphs: [
            'When you sign in, the Extension may process:',
          ],
          bullets: [
            'Your username or email address',
            'Your password, which is transmitted to the applicable Auction.ai authentication endpoint for sign-in',
            'An authentication token',
            'Your authenticated user profile and account role',
            'The backend service URL associated with the Extension',
            'Extension preferences and settings',
          ],
          tail: [
            'The Extension does not intentionally store your password in Chrome extension storage. Authentication tokens, profile information, backend configuration, and settings may be stored locally through chrome.storage.local.',
          ],
        },
        {
          title: 'B. Website and Page Content',
          paragraphs: [
            'When you activate a scanning, manifest-capture, image-analysis, or price-estimation feature, the Extension may access and process information from the active browser tab, including:',
          ],
          bullets: [
            'The current webpage URL',
            'Image URLs and images displayed on the page',
            'Text you select or submit',
            'Relevant product descriptions, titles, or other visible page content',
            'Page HTML when required to perform a user-requested scan',
            'Auction manifest content or portions of a manifest',
            'Other webpage content needed to identify and analyze items',
          ],
          tail: [
            'The Extension accesses and transmits this information only as needed to provide features that you intentionally activate. The Extension is not intended to continuously monitor or collect your general browsing history.',
          ],
        },
        {
          title: 'C. Images and Analysis Requests',
          paragraphs: [
            'When you request an item analysis, the Extension may:',
          ],
          bullets: [
            'Download selected images from the webpage on which they appear',
            'Upload those images to the configured Auction.ai backend',
            'Send relevant text, item descriptions, contextual information, and webpage information',
            'Send the Extension’s selected analysis options and runtime settings',
            'Submit manifest information when you use a manifest-related feature',
          ],
        },
        {
          title: 'D. Analysis Results',
          paragraphs: [
            'The Extension may receive, display, and store information generated through the Auction.ai service, including:',
          ],
          bullets: [
            'Detected products or items',
            'Item classifications and descriptions',
            'Estimated prices, values, or value ranges',
            'Comparable-product information',
            'Confidence scores',
            'Scan and manifest-detection results',
            'Usage or processing metadata',
            'Cost-related metadata',
            'Error messages and diagnostic information',
          ],
        },
        {
          title: 'E. Technical and Diagnostic Information',
          paragraphs: [
            'The Extension and its connected backend may process technical information needed to operate, secure, and troubleshoot the service, such as:',
          ],
          bullets: [
            'Request timestamps',
            'Error reports',
            'Authentication events',
            'API request and response information',
            'Device, browser, or Extension version information',
            'Security and fraud-prevention signals',
            'Local browser-console or debugging messages',
            'Server logs and operational telemetry',
          ],
          tail: [
            'Local console messages generally remain on your device unless they are separately submitted to us or transmitted through a diagnostic feature.',
          ],
        },
      ],
    },
    {
      title: '2. How We Use Information',
      paragraphs: [
        'Auction.ai may use information processed through the Extension to:',
      ],
      bullets: [
        'Authenticate users and maintain signed-in sessions',
        'Confirm account permissions and authorized account roles',
        'Provide item identification, price estimation, and related analysis',
        'Scan user-selected auction pages, images, and manifests',
        'Locate relevant product information and comparable listings',
        'Display and improve analysis results',
        'Save user preferences and Extension settings',
        'Operate, maintain, secure, and troubleshoot the Extension',
        'Detect fraud, misuse, unauthorized access, or technical problems',
        'Monitor service performance and reliability',
        'Provide customer support',
        'Comply with applicable legal obligations',
      ],
      tail: [
        'We do not use webpage content or browsing information collected through the Extension for advertising, creditworthiness determinations, or purposes unrelated to the Extension’s disclosed user-facing functionality.',
      ],
    },
    {
      title: '3. Chrome Web Store Limited-Use Disclosure',
      paragraphs: [
        'Auction.ai’s use and transfer of information received through the Extension will comply with the Chrome Web Store User Data Policy, including its Limited Use requirements.',
        'Information obtained through the Extension is used only to provide or improve the Extension’s disclosed functionality, maintain security, provide support, or comply with applicable law.',
        'We do not sell Extension user data or use it for personalized advertising.',
      ],
    },
    {
      title: '4. When Information Is Processed',
      paragraphs: [
        'Website content is accessed when you use or activate a feature that requires it, such as:',
      ],
      bullets: [
        'Scanning the active page',
        'Selecting an image or text',
        'Requesting a price estimate',
        'Capturing or analyzing a manifest',
        'Requesting comparable-item information',
      ],
      tail: [
        'Depending on the Extension’s configuration, certain content may be prefetched to improve the speed of a user-requested feature. Any such prefetching is limited to information relevant to the Extension’s disclosed functionality.',
      ],
    },
    {
      title: '5. How We Share Information',
      paragraphs: [
        'The Extension sends applicable information to the configured Auction.ai backend so that requested features can be performed.',
        'Auction.ai may disclose relevant information to service providers that assist us with operating the Extension and its backend, including:',
      ],
      bullets: [
        'Artificial-intelligence and machine-learning service providers',
        'Product-data, marketplace, or comparable-listing providers, including eBay APIs when enabled',
        'Cloud hosting and data-storage providers',
        'Database and content-delivery providers',
        'Authentication and account-management providers',
        'Error-monitoring, security, and operational-telemetry providers',
        'Professional advisers or authorities when legally required',
      ],
      tail: [
        'These providers receive only the information reasonably necessary to perform services for Auction.ai and are expected to process it subject to contractual or legal obligations.',
        'Information may also be disclosed:',
      ],
      tailBullets: [
        'To comply with a law, regulation, subpoena, court order, or lawful government request',
        'To investigate fraud, security incidents, or violations of our terms',
        'To protect the rights, property, or safety of Auction.ai, our users, or others',
        'As part of a merger, acquisition, financing, restructuring, bankruptcy, or sale of some or all of our business or assets',
      ],
      closing: [
        'Auction.ai does not sell personal information collected through the Extension for monetary consideration.',
      ],
    },
    {
      title: '6. Configured Backends',
      paragraphs: [
        'Some versions of the Extension may allow an authorized user or administrator to select or configure the backend server used by the Extension.',
        'When the Extension is connected to a backend that is not operated directly by Auction.ai, information submitted through the Extension may also be subject to the privacy, security, and retention practices of the organization operating that backend.',
        'Users should connect the Extension only to trusted and authorized backend deployments.',
      ],
    },
    {
      title: '7. Data Storage and Retention',
      subsections: [
        {
          title: 'Local Extension Storage',
          paragraphs: [
            'Authentication tokens, user profile information, backend configuration, and Extension settings may remain in chrome.storage.local until:',
          ],
          bullets: [
            'You sign out',
            'You clear the Extension’s stored data',
            'The Extension removes the information during an update or reset',
            'You uninstall the Extension',
          ],
          tail: [
            'Signing out is intended to remove locally stored authentication and session information used by the Extension. Certain non-account preferences may remain unless separately cleared.',
          ],
        },
        {
          title: 'Backend Storage',
          paragraphs: [
            'Information transmitted to an Auction.ai backend may be retained for as long as reasonably necessary to:',
          ],
          bullets: [
            'Provide the requested analysis',
            'Maintain account and transaction records',
            'Provide support',
            'Secure and troubleshoot the service',
            'Prevent fraud or abuse',
            'Satisfy contractual requirements',
            'Comply with legal obligations',
          ],
          tail: [
            'Actual retention periods may vary depending on the type of information, the connected backend deployment, and its configuration.',
            'When information is no longer reasonably required, it may be deleted, anonymized, or aggregated, subject to technical, legal, and backup-retention requirements.',
          ],
        },
      ],
    },
    {
      title: '8. Data Security',
      paragraphs: [
        'Auction.ai uses reasonable administrative, technical, and organizational safeguards designed to protect information processed through the Extension. These may include:',
      ],
      bullets: [
        'Token-based authentication',
        'Backend authorization and account-role checks',
        'Access controls',
        'Server-side credential protection',
        'Logging and security monitoring',
        'Encryption in transit when HTTPS is used',
      ],
      tail: [
        'The official Auction.ai backend should be accessed through HTTPS. Users should not configure the Extension to transmit information to an unsecured or untrusted server.',
        'No data-storage or transmission system can be guaranteed to be completely secure. Therefore, we cannot guarantee the absolute security of information processed through the Extension.',
      ],
    },
    {
      title: '9. Your Choices and Rights',
      paragraphs: [
        'Depending on your location, you may have rights regarding your personal information, including the right to request access, correction, deletion, or a copy of certain information.',
        'You may also:',
      ],
      bullets: [
        'Sign out of the Extension',
        'Stop using scanning, manifest, or analysis features',
        'Clear locally stored Extension data through Chrome',
        'Uninstall the Extension',
        'Contact us to request assistance with account or backend data',
      ],
      tail: [
        'Certain information may be retained when required by law or when reasonably necessary for security, fraud prevention, dispute resolution, or enforcement of our agreements.',
      ],
    },
    {
      title: '10. Deleting Extension Data',
      paragraphs: [
        'To remove locally stored Extension information, you may:',
      ],
      numbered: [
        'Sign out through the Extension',
        'Clear the Extension’s site or storage data through Chrome’s extension settings',
        'Uninstall the Extension',
      ],
      tail: [
        'To request deletion of information associated with an Auction.ai account or the official Auction.ai backend, contact us using the information in the Contact Us section below.',
        'Requests involving a separately operated backend should be directed to the organization that controls that backend.',
      ],
    },
    {
      title: '11. Children’s Privacy',
      paragraphs: [
        'The Extension is intended for business and general-audience use. It is not directed to children under 13, and Auction.ai does not knowingly collect personal information through the Extension from children under 13.',
        'If we learn that we have collected personal information from a child in violation of applicable law, we will take reasonable steps to delete it.',
      ],
    },
    {
      title: '12. International Processing',
      paragraphs: [
        'Auction.ai and its service providers may process information in the United States and other countries. These countries may have privacy and data-protection laws that differ from those in your jurisdiction.',
        'Where required, Auction.ai will use appropriate safeguards for international transfers of personal information.',
      ],
    },
    {
      title: '13. Changes to This Privacy Policy',
      paragraphs: [
        'We may update this Privacy Policy to reflect changes to:',
      ],
      bullets: [
        'The Extension’s functionality',
        'Our data practices',
        'Service providers',
        'Legal or regulatory requirements',
        'Security and operational practices',
      ],
      tail: [
        'When we make changes, we will revise the "Last updated" date at the top of this policy. Material changes may also be communicated through the Extension, the Chrome Web Store listing, the Auction.ai website, or another appropriate method.',
      ],
    },
    {
      title: '14. Contact Us',
      paragraphs: [
        'For questions, privacy requests, or concerns regarding this Extension, contact:',
      ],
      bullets: [
        'American Liquidations LLC',
        'Doing business as Auction.ai',
        'Email: sales@americanliquidations.com',
        'Mailing address: 204 Austin Rd, Waterbury, CT 06705',
        'Website: auction.ai',
      ],
    },
  ],
};

export default privacyPolicyContent;
